import { isReleaseName } from "idfweb-assets";
import { DEFAULT_TARGET, type IdfTargets } from "idfweb-build";
import { defineSetting, settings, type Deployment, type Setting } from "idfweb-settings";

import { startExampleOf, type ExampleSetName } from "./project-url.ts";

export { configurationName } from "./project-url.ts";

/**
 * The deployment's knobs and the configuration that sets them.
 *
 * Each knob is a hidden setting: the settings tab leaves it out, and its value
 * comes from the `settings` section of the configuration the page was opened
 * with. See "Deployment configurations" in packages/README.md.
 */

const GROUP = ["Deployment"];

const visibleTargets = defineSetting({
    key: "idfweb.targets.visible",
    type: "list",
    title: "Chip Targets",
    description: "The targets the target picker offers. Empty offers every target of the asset set.",
    group: GROUP,
    default: [],
    hidden: true,
});

const defaultTargetSetting = defineSetting({
    key: "idfweb.targets.default",
    type: "string",
    title: "Default Target",
    description: "The target `auto` builds for until a board or a project sets one.",
    group: GROUP,
    default: "",
    hidden: true,
});

const idfExamplesInclude = defineSetting({
    key: "idfweb.examples.idf.include",
    type: "list",
    title: "ESP-IDF Examples",
    description: "Path patterns of the ESP-IDF examples offered. Empty offers all of them.",
    group: GROUP,
    default: [],
    hidden: true,
});

const idfExamplesExclude = defineSetting({
    key: "idfweb.examples.idf.exclude",
    type: "list",
    title: "Hidden ESP-IDF Examples",
    description: "Path patterns of the ESP-IDF examples left out.",
    group: GROUP,
    default: [],
    hidden: true,
});

const suggestedExamples = defineSetting({
    key: "idfweb.examples.suggested",
    type: "list",
    title: "Suggested Examples",
    description: "ESP-IDF example paths listed first, in a set of their own.",
    group: GROUP,
    default: [],
    hidden: true,
});

export const emulatorEnabled = defineSetting({
    key: "idfweb.connectors.emulator",
    type: "boolean",
    title: "Emulator",
    description: "Offer the emulator in the Connect menu.",
    group: GROUP,
    default: true,
    hidden: true,
});

export const foldersOffered = defineSetting({
    key: "idfweb.welcome.folders",
    type: "boolean",
    title: "Folders on This Computer",
    description: "Offer opening a folder on this computer in the welcome window.",
    group: GROUP,
    default: true,
    hidden: true,
});

export const idfExamplesOffered = defineSetting({
    key: "idfweb.examples.idf.offered",
    type: "boolean",
    title: "Offer ESP-IDF Examples",
    description: "Offer ESP-IDF's examples, and the suggested ones, in the welcome window.",
    group: GROUP,
    default: true,
    hidden: true,
});

const idfReleaseSetting = defineSetting({
    key: "idfweb.idf.release",
    type: "string",
    title: "ESP-IDF Release",
    description:
        "The ESP-IDF release the page builds with, such as `v6.1`, from the site's release sets. Empty for the site's default.",
    group: GROUP,
    default: "",
    hidden: true,
});

/**
 * The ESP-IDF release the configuration chose, or undefined for the site's
 * default and for a name that cannot be one, which loadDeployment reports.
 * Read once at start: the guest is laid out for one release.
 */
export function idfRelease(): string | undefined {
    const release = idfReleaseSetting.get();
    return release === "" || !isReleaseName(release) ? undefined : release;
}

export type ToolchainChoice = "auto" | "clang" | "gcc";

const toolchainSettings = new Map<string, Setting<ToolchainChoice>>();

/**
 * The toolchain a chip builds with: `auto` leaves it to the asset set. Declared
 * per chip once the targets are known.
 */
export function toolchainSetting(target: string): Setting<ToolchainChoice> {
    let setting = toolchainSettings.get(target);
    if (setting === undefined) {
        setting = defineSetting<ToolchainChoice>({
            key: `idfweb.toolchain.${target}`,
            type: "enum",
            title: `Toolchain for ${target}`,
            description: "The compiler and linker this chip builds with.",
            group: GROUP,
            default: "auto",
            options: [
                { value: "auto", label: "As the asset set says" },
                { value: "clang", label: "Clang and lld" },
                { value: "gcc", label: "GCC and GNU ld" },
            ],
            hidden: true,
        });
        toolchainSettings.set(target, setting);
    }
    return setting;
}

/** The chips whose toolchain the deployment chose, for the runtime worker. */
export function chosenToolchains(targets: IdfTargets): Record<string, "clang" | "gcc"> {
    const chosen: Record<string, "clang" | "gcc"> = {};
    for (const target of [...targets.supported, ...targets.preview]) {
        const choice = toolchainSetting(target).get();
        if (choice !== "auto") chosen[target] = choice;
    }
    return chosen;
}

/** The asset set's targets that the target picker offers. */
export function shownTargets(targets: IdfTargets): IdfTargets {
    return { supported: targets.supported.filter(targetShown), preview: targets.preview.filter(targetShown) };
}

/** Whether the target picker offers `target`. */
export function targetShown(target: string): boolean {
    const visible = visibleTargets.get();
    return visible.length === 0 || visible.includes(target);
}

/** The target `auto` stands for: the deployment's, else ESP-IDF's first visible one. */
export function defaultTarget(): string {
    const chosen = defaultTargetSetting.get();
    if (chosen !== "") return chosen;
    const visible = visibleTargets.get();
    return visible.length === 0 || visible.includes(DEFAULT_TARGET) ? DEFAULT_TARGET : visible[0]!;
}

/** Whether an ESP-IDF example at `path` is offered. */
export function idfExampleShown(path: string): boolean {
    const include = idfExamplesInclude.get();
    if (include.length > 0 && !include.some((pattern) => matchesPath(pattern, path))) return false;
    return !idfExamplesExclude.get().some((pattern) => matchesPath(pattern, path));
}

export function suggestedIdfExamples(): readonly string[] {
    return suggestedExamples.get();
}

/**
 * Whether `pattern` matches `path` or a directory above it. `*` matches within
 * one path segment and `**` across segments.
 */
export function matchesPath(pattern: string, path: string): boolean {
    const source = pattern
        .replace(/\/+$/, "")
        .split("**")
        .map((part) => part.split("*").map(escapeRegExp).join("[^/]*"))
        .join(".*");
    return new RegExp(`^${source}(?:/.*)?$`).test(path);
}

function escapeRegExp(text: string): string {
    return text.replace(/[.+?^${}()|[\]\\]/g, "\\$&");
}

/** A configuration file: see packages/README.md. */
interface Configuration {
    settings?: Deployment;
    title?: unknown;
    welcome?: unknown;
    examples?: unknown;
    start?: unknown;
}

/** What a configuration puts on the page besides its settings. */
export interface StartPage {
    /** The tab's title and the introduction's heading. */
    title?: string;
    /** Markdown that replaces the introduction's text. */
    welcome?: string;
    /** The configuration's own examples: the set's label and the URL of its directory. */
    examples?: { label: string; url: string };
    /** The example the introduction's Start button opens. */
    start?: { set: ExampleSetName; path: string };
    configSource?: { label: string; url: string };
}

export interface LoadedDeployment {
    /** What went wrong, for the page to report. */
    problem?: string;
    start: StartPage;
}

const NAME = /^[A-Za-z0-9_-][A-Za-z0-9._-]*$/;
/** A public GitHub repository, "<owner>/<repo>[@<ref>]"; the server's pattern in web/src/github.ts. */
const REPOSITORY = /^([A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\/[A-Za-z0-9_.-]+)(?:@[A-Za-z0-9_.-]+)?$/;
/** A file inside the configuration's directory. */
const FILE = /^(?!.*(?:^|\/)\.)[A-Za-z0-9._/-]+$/;

/**
 * Read the configuration `name`, `default` without one, from the site's
 * `configs` directory, or from `githubUrl` for a repository, apply its
 * settings and return what else it sets. A missing default is fine; any
 * other problem is returned and skipped.
 */
export async function loadDeployment(
    configsUrl: string,
    asked: string | undefined,
    githubUrl?: string,
): Promise<LoadedDeployment> {
    const loaded = await readConfiguration(configsUrl, asked, githubUrl);
    const release = idfReleaseSetting.get();
    if (release !== "" && !isReleaseName(release)) {
        loaded.problem ??= `"${release}" is not an ESP-IDF release; the page uses the site's default one.`;
    }
    return loaded;
}

async function readConfiguration(
    configsUrl: string,
    asked: string | undefined,
    githubUrl?: string,
): Promise<LoadedDeployment> {
    const name = asked ?? "default";
    const repository = REPOSITORY.exec(name);
    if (!NAME.test(name) && (repository === null || githubUrl === undefined)) {
        return { problem: `The configuration "${name}" does not exist.`, start: {} };
    }
    const dir = repository === null ? `${configsUrl}/${name}` : `${githubUrl}/${name}`;
    let configuration: Configuration;
    let commit: string | null = null;
    try {
        const response = await fetch(`${dir}/config.json`, { cache: "no-cache" });
        if (response.status === 404 && asked === undefined) return { start: {} };
        if (!response.ok) {
            return { problem: `The configuration "${name}" could not be read (${response.status}).`, start: {} };
        }
        configuration = (await response.json()) as Configuration;
        commit = response.headers.get("x-idfweb-commit");
    } catch (e) {
        return { problem: `The configuration "${name}" could not be read: ${message(e)}`, start: {} };
    }
    if (configuration.settings !== undefined) settings.applyDeployment(configuration.settings);

    const start: StartPage = {};
    if (repository !== null && commit !== null)
        start.configSource = { label: repository[1]!, url: `https://github.com/${repository[1]}/tree/${commit}` };
    const text = (value: unknown) => (typeof value === "string" && value !== "" ? value : undefined);
    const title = text(configuration.title);
    if (title !== undefined) start.title = title;
    const examples = text(configuration.examples);
    if (examples !== undefined) start.examples = { label: examples, url: `${dir}/examples` };
    let problem: string | undefined;
    if (configuration.start !== undefined) {
        const example = typeof configuration.start === "string" ? startExampleOf(configuration.start) : undefined;
        if (example !== undefined) start.start = example;
        else problem = `The start example of "${name}" is not "builtin/<path>" or "esp-idf/<path>".`;
    }
    const welcome = text(configuration.welcome);
    if (welcome === undefined) return problem === undefined ? { start } : { problem, start };
    try {
        if (!FILE.test(welcome)) throw new Error(`"${welcome}" is not a file in its directory`);
        const response = await fetch(`${dir}/${welcome}`, { cache: "no-cache" });
        if (!response.ok) throw new Error(`${welcome}: ${response.status}`);
        start.welcome = await response.text();
    } catch (e) {
        return { problem: `The welcome text of "${name}" could not be read: ${message(e)}`, start };
    }
    return problem === undefined ? { start } : { problem, start };
}

function message(e: unknown): string {
    return e instanceof Error ? e.message : String(e);
}
